<template>
  <md-dialog :md-active.sync="active" class="whitelist-modal" @md-closed="onClosed">
    <md-dialog-title>
      {{ isEdit ? 'Edit Whitelist Entry' : 'Add to Biometric Whitelist' }}
    </md-dialog-title>

    <md-dialog-content>
      <form @submit.prevent="submit">
        <div class="modal-content">
          <!-- Target Type -->
          <div class="form-field">
            <label class="field-label">Target Type *</label>
            <select-input
                v-model="form.target_type"
                track-by="id"
                name="name"
                :options="targetTypeOptions"
                :allow-empty="true"
                :reduce-to-value="true"
                :filterable="true"
                @option:selected="onTargetTypeChange"
            />
            <span class="md-error" v-if="errors.target_type">{{ errors.target_type }}</span>
          </div>

          <!-- Target -->
          <div class="form-field">
            <label class="field-label">Target *</label>
            <select-input
                v-model="form.target_id"
                track-by="id"
                name="name"
                :placeholder="targetPlaceholder"
                :options="availableTargets"
                :allow-empty="true"
                :disabled="!form.target_type"
                :filterable="!isServerSearch"
                :loading="isSearchingTargets"
                :reduce-to-value="true"
                @search="onTargetSearch"
                @open="onTargetDropdownOpen"
                @option:selected="onTargetOptionSelected"
            >
              <template #option="option">
                <div class="option-row">
                  <strong>{{ option.name }}</strong>
                  <small v-if="option.emp_code">{{ option.emp_code }}</small>
                </div>
              </template>

              <template #no-options="{ search, searching, loading }">
                <div class="no-options">
                  <span v-if="loading">Searching...</span>
                  <span v-else-if="search && search.length < 2">Type at least 2 characters</span>
                  <span v-else-if="searching">No labourers match "{{ search }}"</span>
                  <span v-else>Type to search labourers...</span>
                </div>
              </template>
            </select-input>
            <span class="md-error" v-if="errors.target_id">{{ errors.target_id }}</span>
          </div>

          <!-- Start Date -->
          <div class="form-field">
            <label class="field-label">Start Date *</label>
            <md-field :class="{ 'md-invalid': errors.start_date }">
              <md-input type="date" v-model="form.start_date" required />
              <span class="md-error" v-if="errors.start_date">{{ errors.start_date }}</span>
            </md-field>
          </div>

          <!-- End Date -->
          <div class="form-field">
            <label class="field-label">End Date (Optional — leave empty for permanent)</label>
            <md-field>
              <md-input type="date" v-model="form.end_date" />
            </md-field>
          </div>

          <!-- Reason -->
          <div class="form-field">
            <label class="field-label">Reason (Optional)</label>
            <md-field>
              <md-textarea v-model="form.reason" rows="3" />
            </md-field>
          </div>

          <!-- General Error -->
          <div v-if="generalError" class="error-banner">
            <md-icon>error</md-icon>
            <span>{{ generalError }}</span>
          </div>
        </div>
      </form>
    </md-dialog-content>

    <md-dialog-actions>
      <md-button
          class="md-primary"
          @click="submit"
          :disabled="isSubmitting"
      >
        {{ isSubmitting ? 'Saving...' : (isEdit ? 'Update' : 'Add to Whitelist') }}
      </md-button>
      <md-button class="md-danger" @click="close">Cancel</md-button>
    </md-dialog-actions>
  </md-dialog>
</template>

<script>
import SelectInput from '@/components/Inputs/SelectInput.vue';

export default {
  name: 'WhitelistFormModal',
  components: { SelectInput },

  props: {
    // v-model for visibility
    value: {
      type: Boolean,
      default: false
    },
    // Pass the item being edited (null for create mode)
    item: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      isSubmitting: false,
      generalError: null,
      errors: {},

      form: {
        target_type: 'workplace',
        target_id: null,
        start_date: null,
        end_date: null,
        reason: ''
      },

      // Target type options
      targetTypeOptions: [
        { id: 'station', name: 'Station' },
        { id: 'workplace', name: 'Workplace' },
        { id: 'port', name: 'Port' },
        { id: 'department', name: 'Department' },
        { id: 'labourer', name: 'Labourer' }
      ],

      // AJAX search
      availableTargets: [],
      isSearchingTargets: false,
      searchDebounceTimer: null,

      // Track whether target type is labourer (server-side search)
      isLabourerTarget: false
    };
  },

  computed: {
    active: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },

    isEdit() {
      return !!(this.item && this.item.id);
    },

    isServerSearch() {
      return this.form.target_type === 'labourer';
    },

    targetPlaceholder() {
      if (!this.form.target_type) return 'Select target type first';
      if (this.isServerSearch) return 'Type to search labourers...';
      return 'Select target';
    }
  },

  watch: {
    // Modal opened/closed
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.initializeForm();
        }
      }
    }
  },

  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },

  methods: {
    // ============ INITIALIZATION ============
    initializeForm() {
      this.errors = {};
      this.generalError = null;

      if (this.isEdit && this.item) {
        this.form = {
          target_type: this.item.target_type,
          target_id: this.item.target_id,
          start_date: this.toDateInput(this.item.start_date),
          end_date: this.item.end_date ? this.toDateInput(this.item.end_date) : null,
          reason: this.item.reason || ''
        };
      } else {
        this.form = {
          target_type: 'workplace',
          target_id: null,
          start_date: this.toDateInput(new Date()),
          end_date: null,
          reason: ''
        };
      }

      this.loadAvailableTargets();
    },

    // ============ TARGET LOADING ============
    async loadAvailableTargets() {
      var type = this.form.target_type;

      this.availableTargets = [];

      // Labourers = AJAX search only
      if (type === 'labourer') {
        // If editing, preload the currently-selected labourer
        if (this.isEdit && this.form.target_id) {
          await this.preloadSelectedLabourer(this.form.target_id);
        }
        return;
      }

      try {
        switch (type) {
          case 'station':
            await this.$store.dispatch('stations/list', { page: { size: 1000 } });
            this.availableTargets = this.$store.getters['stations/list'] || [];
            break;
          case 'workplace':
            await this.$store.dispatch('workplaces/list', { page: { size: 1000 } });
            this.availableTargets = this.$store.getters['workplaces/list'] || [];
            break;
          case 'port':
            await this.$store.dispatch('ports/list', { page: { size: 1000 } });
            this.availableTargets = this.$store.getters['ports/list'] || [];
            break;
          case 'department':
            await this.$store.dispatch('departments/list', { page: { size: 1000 } });
            this.availableTargets = this.$store.getters['departments/list'] || [];
            break;
        }
      } catch (error) {
        console.error('Error loading targets:', error);
        this.generalError = 'Failed to load targets';
      }
    },

    async preloadSelectedLabourer(id) {
      try {
        await this.$store.dispatch('labourers/get', { id: id });
        var labourer = this.$store.getters['labourers/labourer'];

        if (labourer) {
          this.availableTargets = [this.mapLabourerToOption(labourer)];
        }
      } catch (e) {
        // Fail silently
      }
    },

    // ============ TARGET TYPE CHANGE ============
    onTargetTypeChange(option) {
      // Get the type ID from either an object or a string
      var newType = option && typeof option === 'object' ? option.id : option;
      if (!newType) return;

      // If type changed, reset target
      if (this.form.target_type !== newType) {
        this.form.target_id = null;
        this.availableTargets = [];
      }

      this.form.target_type = newType;
      this.loadAvailableTargets();
    },

    // ============ AJAX SEARCH ============
    onTargetSearch(query) {
      if (!this.isServerSearch) return;

      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      var self = this;
      this.searchDebounceTimer = setTimeout(function () {
        self.searchLabourers(query);
      }, 350);
    },

    async searchLabourers(query) {
      var trimmed = (query || '').trim();

      if (trimmed.length < 2) {
        this.availableTargets = [];
        return;
      }

      this.isSearchingTargets = true;

      try {
        await this.$store.dispatch('labourers/list', {
          filter: { queryKey: trimmed },
          page: { number: 1, size: 50 }
        });

        var results = this.$store.getters['labourers/list'] || [];
        var self = this;
        this.availableTargets = results.map(function (labourer) {
          return self.mapLabourerToOption(labourer);
        });
      } catch (error) {
        console.error('Error searching labourers:', error);
        this.availableTargets = [];
      } finally {
        this.isSearchingTargets = false;
      }
    },

    async onTargetDropdownOpen() {
      if (!this.isServerSearch) return;

      var targetId = this.form.target_id;
      if (!targetId) return;

      var exists = this.availableTargets.some(function (t) {
        return t.id === targetId;
      });
      if (exists) return;

      await this.preloadSelectedLabourer(targetId);
    },

    onTargetOptionSelected(option) {
      // Hook for future use (e.g., storing the full object)
    },

    mapLabourerToOption(labourer) {
      var firstName = labourer.first_name || '';
      var lastName = labourer.last_name || '';
      var nidaNumber = labourer.nida_number || '';
      return {
        id: labourer.id,
        name: (firstName + ' ' + lastName).trim() + ':'+nidaNumber || labourer.phone_number || 'Unknown',
        emp_code:  labourer.labourer_number || '',
        first_name: firstName,
        last_name: lastName,
        nida_number: labourer.nida_number || '',
        phone_number: labourer.phone_number
      };
    },

    // ============ VALIDATION + SUBMIT ============
    validate() {
      this.errors = {};

      if (!this.form.target_type) {
        this.errors.target_type = 'Target type is required';
      }
      if (!this.form.target_id) {
        this.errors.target_id = 'Target is required';
      }
      if (!this.form.start_date) {
        this.errors.start_date = 'Start date is required';
      }

      return Object.keys(this.errors).length === 0;
    },

    async submit() {
      if (!this.validate()) return;

      this.isSubmitting = true;
      this.generalError = null;

      try {
        var payload = {
          type: 'biometricWhitelists',
          target_type: this.form.target_type,
          target_id: this.form.target_id,
          start_date: this.toDateYMD(this.form.start_date),
          end_date: this.form.end_date ? this.toDateYMD(this.form.end_date) : null,
          reason: this.form.reason
        };

        if (this.isEdit) {
          payload.id = this.item.id;
          await this.$store.dispatch('biometricWhitelists/update', payload);
          this.$emit('updated');
        } else {
          await this.$store.dispatch('biometricWhitelists/add', payload);
          this.$emit('created');
        }

        this.$emit('success', {
          isEdit: this.isEdit,
          item: payload
        });

        this.close();
      } catch (error) {
        console.error('Error saving whitelist:', error);

        if (error.response && error.response.data && error.response.data.errors) {
          var apiErrors = error.response.data.errors;
          if (Array.isArray(apiErrors)) {
            var self = this;
            apiErrors.forEach(function (err) {
              var field = 'general';
              if (err.source && err.source.pointer) {
                field = err.source.pointer.split('/').pop();
              }
              if (field === 'general') {
                self.generalError = err.detail;
              } else {
                self.errors[field] = err.detail;
              }
            });
          } else if (error.response.data.message) {
            this.generalError = error.response.data.message;
          }
        } else {
          this.generalError = 'Failed to save whitelist entry. Please try again.';
        }

        this.$emit('error', error);
      } finally {
        this.isSubmitting = false;
      }
    },

    // ============ CLOSE / RESET ============
    close() {
      this.active = false;
    },

    onClosed() {
      // Reset internal state after close animation finishes
      this.errors = {};
      this.generalError = null;
      this.isSubmitting = false;
      this.availableTargets = [];
      this.isSearchingTargets = false;
      this.form = {
        target_type: 'workplace',
        target_id: null,
        start_date: null,
        end_date: null,
        reason: ''
      };
      this.$emit('closed');
    },

    // ============ HELPERS ============
    toDateInput(date) {
      if (!date) return null;
      var d = new Date(date);
      var year = d.getFullYear();
      var month = String(d.getMonth() + 1).padStart(2, '0');
      var day = String(d.getDate()).padStart(2, '0');
      return year + '-' + month + '-' + day;
    },

    toDateYMD(date) {
      return this.toDateInput(date);
    }
  }
};
</script>

<style lang="scss" scoped>
.whitelist-modal {
  ::v-deep .md-dialog {
    max-width: 520px;
    width: 90%;
  }

  ::v-deep .md-dialog-content {
    padding: 8px 24px 16px;
    overflow: visible;
  }

  ::v-deep .md-dialog-title {
    padding: 20px 24px 0;
    font-size: 18px;
    font-weight: 600;
  }

  ::v-deep .md-dialog-actions {
    padding: 8px 24px 20px;
  }
}

.modal-content {
  min-width: 400px;
  padding-top: 8px;
}

.form-field {
  margin-bottom: 16px;

  .field-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #555;
    margin-bottom: 6px;
  }
}

.option-row {
  display: flex;
  justify-content: space-between;
  align-items: center;

  small {
    font-size: 11px;
    color: #999;
    margin-left: 8px;
  }
}

.no-options {
  padding: 12px;
  text-align: center;
  color: #999;
  font-size: 13px;
}

.error-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #ffebee;
  border-radius: 6px;
  color: #c62828;
  font-size: 13px;

  .md-icon {
    color: #c62828;
    font-size: 18px;
  }
}

.md-error {
  font-size: 12px;
  color: #f44336;
  margin-top: 4px;
  display: block;
}

@media (max-width: 768px) {
  .modal-content {
    min-width: auto;
  }
}
</style>